/**
 * Satatam Help -- the operator-facing guidance assistant.
 *
 * One provider owns the whole surface: the thread, the busy flag, whether the
 * drawer is open, and the `?` shortcut that opens it. Keeping the shortcut
 * here rather than in `AppShell` means any tree that has the provider has the
 * key, which is what lets the browser fixture drive the real thing without
 * standing up the entire shell -- and it removes the second listener the shell
 * would otherwise have to keep in sync with this file's modal rules.
 *
 * The thread lives in `sessionStorage`, per tenant. It is a per-viewer
 * convenience, not a record: a blocked or full store costs the operator their
 * scrollback and nothing else, so every access is wrapped and nothing here
 * waits on it.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";

import { ApiError, helpApi, type HelpLink } from "./api.ts";
import { apiErrorCode, apiErrorNumber } from "./apiError.ts";

export interface HelpMessage {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  links?: HelpLink[];
  /** For a failed turn: the question a Retry should re-send. */
  retryOf?: string;
}

interface HelpValue {
  open: boolean;
  busy: boolean;
  messages: HelpMessage[];
  openWith: (question?: string) => void;
  close: () => void;
  ask: (question: string) => Promise<void>;
  /** Push a local (non-AI) assistant message, e.g. the first-run welcome. */
  say: (text: string, links?: HelpLink[]) => void;
}

const HelpContext = createContext<HelpValue | null>(null);
const THREAD_MAX = 40;
const HISTORY_MAX = 10;
const QUESTION_MAX = 2000;
const CONTACT = { label: "Talk to Satatam", href: "https://satatam.co/contact.html?topic=general" };

/**
 * The destinations the server will accept as `page`. Anything else is sent as
 * `null` rather than guessed at: the backend rejects an unknown page, and a
 * rejected request is a worse answer than an unlocated one.
 */
export const HELP_ALLOWED_PAGES = new Set([
  "/overview", "/playground", "/inbox", "/analytics", "/install", "/settings", "/settings#knowledge",
  "/settings#account", "/settings?tab=team", "/aria", "/assistant", "/appearance", "/engagement",
]);

export const threadStorageKey = (tenantId: string) => `cspro.help.thread.v1:${encodeURIComponent(tenantId)}`;

function readThread(key: string): HelpMessage[] {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((m) => m && typeof m.text === "string") : [];
  } catch { return []; }
}
function writeThread(key: string, messages: HelpMessage[]) {
  try { sessionStorage.setItem(key, JSON.stringify(messages.slice(-THREAD_MAX))); } catch { /* per-viewer convenience */ }
}
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * The current route, spelled the way the server's allowlist spells it. Two
 * sidebar entries differ only by hash and one only by query, so the longest
 * form that the allowlist knows wins.
 */
export function pageForHelp(pathname: string, search: string, hash: string): string | null {
  for (const candidate of [pathname + search + hash, pathname + hash, pathname + search, pathname]) {
    if (HELP_ALLOWED_PAGES.has(candidate)) return candidate;
  }
  return null;
}

const COPY: Record<string, string> = {
  help_unavailable: "Satatam Help is unavailable right now. Try again in a moment.",
  question_too_long: "That question is too long. Keep it under 2,000 characters.",
  question_required: "Ask a question first.",
  history_too_long: "This conversation got too long. Close Satatam Help and open it again to start fresh.",
};

/** The cap the sentence quotes when the 429 body carries none of its own. */
const DEFAULT_DAILY_CAP = 200;

/**
 * The cap sentence is the only thing an operator reads at the moment they are
 * blocked, so it says what the limiter actually does: a 24h window that starts
 * at the tenant's first question, not a midnight reset that no layer performs.
 * The number is the server's (`daily_cap` on the 429 body) so the app does not
 * have to ship again the day the cap moves.
 */
const capCopy = (cap: number) =>
  `You've reached the limit of ${cap} questions for this workspace. It resets 24 hours after the first question in this window.`;

export function HelpProvider({ tenantId, children }: { tenantId: string; children: ReactNode }) {
  const key = threadStorageKey(tenantId);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<HelpMessage[]>(() => (tenantId ? readThread(key) : []));
  const pending = useRef<string | null>(null);
  /** Bumped every time a question is queued -- see `openWith`. */
  const [queued, setQueued] = useState(0);

  /* No tenant, no storage. `tenantId` is "" until the session resolves, and
     `cspro.help.thread.v1:` is a real key that every tenant on the machine
     would otherwise share -- the write effect runs before the read effect
     re-runs, so an unguarded provider persists the previous thread under the
     bare key on the way past. AppShell also keys the provider by tenant id,
     so an account switch remounts this instead of carrying a thread across;
     both halves are needed, because the mount before a session exists is not
     a switch. In memory only until the tenant is known costs the operator
     nothing: there is no thread yet. */
  useEffect(() => { if (tenantId) setMessages(readThread(key)); }, [key, tenantId]);
  useEffect(() => { if (tenantId) writeThread(key, messages); }, [key, messages, tenantId]);

  const say = useCallback((text: string, links?: HelpLink[]) => {
    setMessages((m) => [...m, { id: uid(), role: "assistant", text, links }]);
  }, []);

  const ask = useCallback(async (question: string) => {
    const q = question.trim();
    if (!q || q.length > QUESTION_MAX || busy) return;
    setBusy(true);
    const history = messages
      .filter((m) => m.role !== "system" && !m.retryOf)
      .slice(-HISTORY_MAX)
      .map((m) => ({ role: m.role as "user" | "assistant", text: m.text }));
    setMessages((m) => [...m, { id: uid(), role: "user", text: q }]);
    const page = pageForHelp(location.pathname, location.search, location.hash);
    try {
      /* One message, grown in place. The id is minted before the first delta
         so every later fragment addresses the same bubble -- appending a new
         message per fragment would scroll the drawer on every token and make
         the thread's history a list of syllables. Links arrive last and are
         set on the same message. */
      const answerId = uid();
      /* Whether the bubble exists yet is read from the state the updater was
         handed, never from a variable the updater writes. StrictMode invokes
         an updater twice from the SAME base state and keeps the last result:
         a flag flipped inside the first call would send the second call down
         the "append to the existing bubble" branch, find no such bubble, and
         return the state unchanged -- silently losing the first fragment.
         Appending twice from one base is idempotent; deciding twice from a
         mutated flag is not.

         There is no "nothing came back" branch after this await, and there
         must not be one: a turn that produced no answer arrives as an `error`
         frame, which `askStreaming` throws as the same ApiError a refused
         JSON request throws. The catch below is the one outage path, so the
         outage copy and Retry are identical on both. */
      await helpApi.askStreaming(q, history, page, {
        onDelta: (text) => {
          if (!text) return;
          setMessages((m) => (m.some((msg) => msg.id === answerId)
            ? m.map((msg) => (msg.id === answerId ? { ...msg, text: msg.text + text } : msg))
            : [...m, { id: answerId, role: "assistant" as const, text }]));
        },
        onMeta: ({ links, needs_human }) => {
          const withContact = needs_human && !links.some((l) => l.href === CONTACT.href)
            ? [...links, CONTACT] : links;
          setMessages((m) => (m.some((msg) => msg.id === answerId)
            ? m.map((msg) => (msg.id === answerId ? { ...msg, links: withContact } : msg))
            : [...m, { id: answerId, role: "assistant" as const, text: "", links: withContact }]));
        },
      });
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      const code = (apiError ? apiErrorCode(apiError) : null) ?? "";
      const text = code === "help_daily_cap"
        ? capCopy((apiError && apiErrorNumber(apiError, "daily_cap")) ?? DEFAULT_DAILY_CAP)
        : COPY[code] ?? COPY.help_unavailable;
      const retryable = code !== "help_daily_cap" && code !== "question_too_long";
      setMessages((m) => [...m, {
        id: uid(),
        role: "system",
        text,
        retryOf: retryable ? q : undefined,
        links: code === "help_daily_cap" ? [CONTACT] : undefined,
      }]);
    } finally {
      setBusy(false);
    }
  }, [busy, location.hash, location.pathname, location.search, messages]);

  /**
   * Closing drops anything still queued. A question the operator opened and
   * then walked away from must not arrive later, on top of their next one.
   */
  const close = useCallback(() => {
    pending.current = null;
    setOpen(false);
  }, []);

  /**
   * `openWith(question)` must send whatever state the drawer is in, and
   * `setOpen(true)` alone cannot promise that. When the drawer is ALREADY open
   * and idle, `setOpen(true)` is a no-op, `pending` is a ref so it schedules no
   * render, and none of the send effect's dependencies move -- the question
   * would sit in the ref until some unrelated change re-ran the effect, and
   * then be asked out of context, most likely straight after the operator's
   * next question. `queued` is what guarantees a render: a counter rather than
   * a flag so two calls in a row are two distinct values, and state rather
   * than a ref precisely so the effect below re-runs.
   */
  const openWith = useCallback((question?: string) => {
    setOpen(true);
    if (question) {
      pending.current = question;
      setQueued((n) => n + 1);
    }
  }, []);

  /* The queued question is sent on the first render where the drawer is open
     and idle: the one `queued` itself caused when it was already open, the one
     that opens it, or the one that clears `busy`. The ref is read and cleared
     before `ask`, which is what makes this send exactly once under StrictMode's
     double-invoked effects. */
  useEffect(() => {
    if (!open || !pending.current || busy) return;
    const q = pending.current; pending.current = null;
    void ask(q);
  }, [ask, busy, open, queued]);

  /**
   * `?` opens Help from anywhere it is not already a character being typed.
   * Inbox owns `?` on its own route for the shortcut guide, and any open modal
   * -- including this drawer -- keeps it, so Help never opens on top of
   * something else or twice.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "?" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("input, textarea, select, [contenteditable='true']")) return;
      if (location.pathname.startsWith("/inbox")) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [location.pathname]);

  const value = useMemo<HelpValue>(() => ({
    open, busy, messages, openWith, close, ask, say,
  }), [ask, busy, close, messages, open, openWith, say]);
  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpValue {
  const v = useContext(HelpContext);
  if (!v) throw new Error("useHelp must be used inside HelpProvider");
  return v;
}
